import { formatDayMonth, t } from '@glory/i18n';
import { isoDateIn, minutesOfDayIn } from '@glory/kernel';
import {
  addDays,
  clockText,
  type IsoDate,
  isWithoutStep,
  STEP_KINDS,
  type StepKind,
  type StepPresetKey,
  stepPresets,
  stepStatus,
  type StepStatus,
  type StepTiming,
} from '@glory/sales/domain';
import type { ChoiceOption } from '@glory/ui';

import type { StepFormValue } from './patch.ts';

export const stepKindLabel = (kind: StepKind): string => t(`sales.stepKinds.${kind}`);

export const stepKindOptions = (): ChoiceOption<StepKind>[] =>
  STEP_KINDS.map((kind) => ({ value: kind, label: stepKindLabel(kind) }));

/** Быстрые варианты следующего шага: «Завтра», «Через 3 дня», «Через неделю» — от сегодняшнего дня студии. */
export const presetLabel = (key: StepPresetKey): string => t(`sales.nextStep.presets.${key}`);
export { stepPresets };

/** Сегодняшний день студии: по её часовому поясу, а не по часам устройства. */
export const studioDay = (timeZone: string, now: Date = new Date()): IsoDate => isoDateIn(timeZone, now);

/** Местное время студии `ГГГГ-ММ-ДДTЧЧ:ММ` — для полей «дата и время». */
export function studioDateTime(timeZone: string, now: Date = new Date()): string {
  return `${isoDateIn(timeZone, now)}T${clockText(minutesOfDayIn(timeZone, now))}`;
}

/** Где шаг относительно «сейчас» и как его подписать: «Сегодня, 15:00», «Завтра», «12 октября». */
export interface StepPlace {
  readonly status: StepStatus;
  readonly text: string;
}

export function stepPlace(step: StepTiming, now: Date, timeZone: string): StepPlace {
  const today = isoDateIn(timeZone, now);
  const day =
    step.dueDate === today
      ? t('sales.nextStep.today')
      : step.dueDate === addDays(today, 1)
        ? t('sales.nextStep.tomorrow')
        : step.dueDate === addDays(today, -1)
          ? t('sales.nextStep.yesterday')
          : formatDayMonth(step.dueDate);
  return {
    status: stepStatus(step, now, timeZone),
    text: step.dueTime === null ? day : `${day}, ${step.dueTime}`,
  };
}

type Row = Parameters<typeof isWithoutStep>[0];

/** Запись без шага, которую нужно подсветить: открытая, не отложенная, не лид в очереди. */
export const needsStep = (row: Row, hasStep: boolean): boolean => isWithoutStep(row, hasStep);

// --- черновик шага в форме -------------------------------------------------------------------------------

/** Шаг в форме, пока она не заполнена: дата ещё может быть пустой. */
export interface StepDraft {
  readonly kind: StepKind;
  readonly title: string;
  readonly dueDate: IsoDate | null;
  /** `ЧЧ:ММ`; null — на весь день. */
  readonly dueTime: string | null;
  /** null — по умолчанию: ответственный за запись. */
  readonly assignee: { readonly id: string; readonly name: string } | null;
}

/** Новый шаг: «позвонить» завтра на весь день. */
export const newDraft = (today: IsoDate): StepDraft => ({
  kind: 'call',
  title: '',
  dueDate: addDays(today, 1),
  dueTime: null,
  assignee: null,
});

/** Чего не хватает шагу, чтобы его можно было сохранить: название или дата. */
export type DraftProblem = 'title' | 'date';

export function draftProblem(draft: StepDraft): DraftProblem | null {
  if (draft.title.trim() === '') return 'title';
  if (draft.dueDate === null) return 'date';
  return null;
}

/** Шаг для отправки на сервер; null — форма не заполнена. */
export function draftValue(draft: StepDraft): StepFormValue | null {
  if (draftProblem(draft) !== null || draft.dueDate === null) return null;
  return {
    kind: draft.kind,
    title: draft.title.trim(),
    dueDate: draft.dueDate,
    dueTime: draft.dueTime,
    assignee: draft.assignee,
  };
}
